import { useState } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FileDown,
  Loader2,
  ArrowLeft,
  Building2,
  Calendar,
  AlertTriangle,
  Share2,
  Copy,
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import ReportVisualization from "@/components/reports/ReportVisualization";
// @deprecated - Old client-side PDF generation (kept for reference)
// import { generatePdfReport } from "@/service/pdf-generator";
import { useGenerateTextReport } from "@/hooks/use-report-dashboard";
import { FileText } from "lucide-react";

interface ReportDashboardData {
  assessment: {
    id: string;
    organizationName: string;
    reportingYear: number;
    currency: string;
    sector: string;
    locked: boolean;
    workspaceName?: string;
  };
  carbonPerformance: {
    totalEmissionsTco2e: number;
    scope1Tco2e: number;
    scope2Tco2e: number;
    intensityMetrics: {
      emissionsPerRevenue: number;
      emissionsPerEmployee: number;
      energyIntensity: number;
    };
    reportingPeriod: string;
  };
  esgStatus: {
    environmental: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    social: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
    governance: {
      completeness: number;
      metricsProvided: number;
      totalMetrics: number;
    };
  };
  trustIndicators: {
    completenessPercent: number;
    vsmeLevel: string;
    evidenceCount: number;
    dataQualityRating: number;
    boundaryStatement: string;
  };
  hotspots: Array<{
    source: string;
    tco2e: number;
    percentage: number;
  }>;
  recommendations: Array<{
    priority: string;
    title: string;
    description: string;
  }>;
}

interface ShareLinkResult {
  token: string;
  shareUrl: string;
  expiresAt: string | null;
  createdAt: string;
  created: boolean;
}

type PdfKind = "pdf" | "measures-pdf";

export default function AdminReportDetail({ apiBase = "/api/admin/reports" }: { apiBase?: string }) {
  const { assessmentId } = useParams<{ assessmentId: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { i18n } = useTranslation();

  // Portfolio managers reuse this page read-only: no admin card, no AI text report, plus measures PDF and share
  const isPortfolio = apiBase === "/api/portfolio/reports";
  const backHref = isPortfolio ? `/portfolio/assessments/${assessmentId}` : "/admin/reports";
  const backLabel = isPortfolio ? "Back to Assessment" : "Back to Reports";

  const [generatingPdf, setGeneratingPdf] = useState<PdfKind | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const generateTextReport = useGenerateTextReport();

  // Get-or-create: returns the company's active share link, or creates one
  const shareMutation = useMutation({
    mutationFn: async (): Promise<ShareLinkResult> => {
      const response = await fetch(`${apiBase}/${assessmentId}/share`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || "Failed to get share link");
      }
      return response.json();
    },
    onError: (error: Error) => {
      setShareDialogOpen(false);
      toast({ title: "Share failed", description: error.message, variant: "destructive" });
    },
  });

  const handleShare = () => {
    setShareDialogOpen(true);
    shareMutation.mutate();
  };

  const handleCopyShareLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ title: "Link copied!", description: "Share link copied to clipboard" });
  };

  const handleGenerateTextReport = async () => {
    if (!assessmentId || !reportData) return;
    try {
      const result = await generateTextReport.mutateAsync({ assessmentId, locale: i18n.language });
      setLocation("/report/text", {
        state: {
          report: {
            report: result.report,
            assessmentId,
            organizationName: reportData.assessment.organizationName,
            reportingYear: reportData.assessment.reportingYear.toString(),
          },
        },
      });
    } catch (error: any) {
      toast({
        title: "Failed to generate report",
        description: error.message || "An error occurred",
        variant: "destructive",
      });
    }
  };

  // Fetch report data for admin
  const {
    data: reportData,
    isLoading,
    error,
  } = useQuery<ReportDashboardData>({
    queryKey: ["admin-report-dashboard", apiBase, assessmentId],
    queryFn: async () => {
      if (!assessmentId) {
        throw new Error("Assessment ID is required");
      }

      const response = await fetch(`${apiBase}/${assessmentId}/dashboard`, {
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to fetch report data");
      }

      return response.json();
    },
    enabled: !!assessmentId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Handle PDF generation (server-side via Puppeteer)
  const handleGeneratePdf = async (kind: PdfKind) => {
    if (!reportData) return;

    setGeneratingPdf(kind);
    try {
      const response = await fetch(`${apiBase}/${assessmentId}/${kind}?locale=${encodeURIComponent(i18n.language)}`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF report");
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        response.headers
          .get("Content-Disposition")
          ?.match(/filename="(.+)"/)?.[1] || `${kind === "pdf" ? "report" : "measures_report"}_${assessmentId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);

      toast({
        title: "PDF Generated",
        description: "The report PDF has been generated successfully.",
      });
    } catch (error: any) {
      console.error("PDF generation error:", error);
      toast({
        title: "PDF Generation Failed",
        description: error.message || "Failed to generate PDF report",
        variant: "destructive",
      });
    } finally {
      setGeneratingPdf(null);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <div className="space-y-6">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto py-8 px-4 max-w-7xl">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{"Error Loading Report"}</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : "Failed to load report data"}
          </AlertDescription>
        </Alert>
        <Button
          onClick={() => setLocation(backHref)}
          className="mt-4"
          variant="outline"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          {backLabel}
        </Button>
      </div>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <Button
              onClick={() => setLocation(backHref)}
              variant="ghost"
              size="sm"
              className="mb-2"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Button>
            <h1 className="text-3xl font-bold text-[#001F24]">
              {isPortfolio ? "Report View" : "Platform Admin - Report View"}
            </h1>
            <div className="flex items-center gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span>{reportData.assessment.organizationName}</span>
              </div>
              {reportData.assessment.workspaceName && (
                <Badge variant="outline">
                  {reportData.assessment.workspaceName}
                </Badge>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>
                  {`Reporting Year: ${reportData.assessment.reportingYear}`}
                </span>
              </div>
              <Badge variant={reportData.assessment.locked ? "default" : "secondary"}>
                {reportData.assessment.locked
                  ? "Locked"
                  : "Draft"}
              </Badge>
            </div>
          </div>

          <div className="flex gap-2">
            {isPortfolio ? (
              <Button variant="outline" onClick={handleShare}>
                <Share2 className="mr-2 h-4 w-4" />
                {"Share report"}
              </Button>
            ) : (
              <Button
                variant="outline"
                onClick={handleGenerateTextReport}
                disabled={generateTextReport.isPending}
              >
                {generateTextReport.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {"Generating..."}
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    {"Generate Text Report"}
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={() => handleGeneratePdf("pdf")}
              disabled={generatingPdf !== null}
              className="bg-[#00444C] hover:bg-[#003038]"
            >
              {generatingPdf === "pdf" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {"Generating PDF..."}
                </>
              ) : (
                <>
                  <FileDown className="mr-2 h-4 w-4" />
                  {"Generate PDF"}
                </>
              )}
            </Button>
            {isPortfolio && (
              <Button
                onClick={() => handleGeneratePdf("measures-pdf")}
                disabled={generatingPdf !== null}
                className="bg-[#00444C] hover:bg-[#003038]"
              >
                {generatingPdf === "measures-pdf" ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {"Generating PDF..."}
                  </>
                ) : (
                  <>
                    <FileDown className="mr-2 h-4 w-4" />
                    {"Download measures PDF"}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Info Card */}
        {!isPortfolio && (
          <Card className="border-[#7ECCC5] bg-blue-50">
            <CardHeader>
              <CardTitle className="text-[#009687]">{"Platform Administrator View"}</CardTitle>
              <CardDescription>
                {"You are viewing this report as a platform administrator. This report belongs to"}{" "}
                <strong>{reportData.assessment.organizationName}</strong>
                {reportData.assessment.workspaceName && (
                  <>
                    {" "}
                    {"in workspace"}{" "}
                    <strong>{reportData.assessment.workspaceName}</strong>
                  </>
                )}.
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        {/* Report Visualization */}
        <ReportVisualization data={reportData} showStatusAlert={false} />
      </div>

      {/* Share Dialog (portfolio mode) */}
      {isPortfolio && (
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Share2 className="h-5 w-5" />
                {"Share Report"}
              </DialogTitle>
              <DialogDescription>
                {"Anyone with this link can view the report."}
              </DialogDescription>
            </DialogHeader>
            {shareMutation.data && !shareMutation.isPending ? (
              <div className="space-y-3 py-4">
                <div className="flex items-center gap-2">
                  <Input value={shareMutation.data.shareUrl} readOnly className="font-mono text-sm" />
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => shareMutation.data && handleCopyShareLink(shareMutation.data.shareUrl)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  {shareMutation.data.created
                    ? "A new share link was created."
                    : "This is the company's existing share link."}
                </p>
              </div>
            ) : (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
